import { useParams } from 'react-router-dom';
import { usePublicEvent } from '@/hooks/useProducerEvents';
import { useProducer } from '@/hooks/useProducer';
import { useCoverImageUrl } from '@/hooks/useMediaAssets';
import { PublicEventCard } from '@/components/public/PublicEventCard';
import { Skeleton } from '@/components/ui/skeleton';
import { resolveVenueText } from '@/lib/venue/resolveVenueText';
import { activityDetailPath } from '@/lib/paths';

export default function ActivityEmbedPage() {
  const { id } = useParams<{ id: string }>();
  const { data: event, isLoading: eventLoading } = usePublicEvent(id);
  const { producer, loading: producerLoading } = useProducer(event?.producer_id || '');

  const coverUrl = useCoverImageUrl('event', event?.id ?? null, 640);
  const producerFallbackCover = useCoverImageUrl('producer', !coverUrl ? (event?.producer_id ?? null) : null, 640);

  const isLoading = eventLoading || (event ? producerLoading : false);

  if (isLoading) {
    return (
      <main className="min-h-screen bg-background/40 p-3">
        <Skeleton className="h-[360px] rounded-xl" />
      </main>
    );
  }

  if (!event) {
    return (
      <main className="min-h-screen bg-background/40 p-4">
        <section className="rounded-xl border border-border/40 bg-background/80 p-6 text-center">
          <p className="text-sm text-muted-foreground">Aktiviteten hittades inte.</p>
        </section>
      </main>
    );
  }

  const venue = resolveVenueText({
    use_producer_address: (event as any).use_producer_address,
    custom_address: (event as any).custom_address,
    producer_name: producer?.name,
    producer_city: producer?.city,
  });

  return (
    <main className="min-h-screen bg-background/40 p-3 sm:p-4">
      <PublicEventCard
        className="shadow-none"
        event={{
          id: event.id,
          title: event.title,
          startDate: event.start_date,
          endDate: event.end_date,
          timingMode: ((event as any).timing_mode || 'date_time') as 'date_time' | 'open' | 'anytime' | 'season',
          outingTags: (event as any).outing_tags ?? [],
          producerName: producer?.name ?? null,
          producerCity: producer?.city ?? null,
          location: venue.venue_text,
          eventType: ((event as any).event_type as 'event' | 'course') || 'event',
          includesFarmSale: (event as any).includes_farm_sale ?? false,
          includesFarmSaleAlcohol: (event as any).includes_farm_sale_alcohol ?? false,
          occurrencesCount: (event as any).occurrences_count ?? 0,
        }}
        coverImageUrl={coverUrl}
        fallbackCoverUrl={producerFallbackCover}
        onClick={() => {
          window.open(activityDetailPath(event.id), '_blank', 'noopener,noreferrer');
        }}
      />
    </main>
  );
}
